import React, { useState } from 'react';
import { Offcanvas, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faCheckCircle,
  faExclamationTriangle,
  faUserFriends,
  faChevronDown,
  faChevronUp,
  faBoxOpen,
  faStickyNote,
  faHistory,
  faUsers,
  faEye,
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import SubtleBadge from 'components/common/subtleBadge';
import { useNavigate } from 'react-router-dom';

const formatAddress = (relation) => {
  if (!relation) return '--';
  const streetWithNumber = [relation.StreetName, relation.HouseNumber].filter(Boolean).join(' ');
  const postCodeWithCity = [relation.PostCode, relation.City].filter(Boolean).join(' ');
  const parts = [streetWithNumber, postCodeWithCity].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '--';
};

const AVATAR_COLORS = [
  { bg: '#e8f5e9', text: '#2e7d32' },
  { bg: '#e3f2fd', text: '#1565c0' },
  { bg: '#fff3e0', text: '#e65100' },
  { bg: '#f3e5f5', text: '#6a1b9a' },
  { bg: '#fce4ec', text: '#880e4f' },
  { bg: '#e0f7fa', text: '#006064' },
  { bg: '#fff8e1', text: '#f57f17' },
];

const getAvatarColor = (name = '') => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getInitials = (name = '') =>
  name.split(' ').filter(Boolean).map((w) => w[0]).join('').substring(0, 2).toUpperCase();

const AccordionSection = ({ icon, title, badge, badgeBg = 'primary', defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mb-3">
      <div
        className="d-flex align-items-center justify-content-between py-2 cursor-pointer"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="d-flex align-items-center gap-2">
          {icon && <FontAwesomeIcon icon={icon} className="text-primary font-size-13" />}
          <h3 className="fw-medium font-size-15 mb-0">{title}</h3>
        </div>
        <div className="d-flex align-items-center gap-2">
          {badge && (
            typeof badge === 'string'
              ? <SubtleBadge pill bg={badgeBg} className="fw-medium font-size-12 d-flex align-items-center gap-1">{badge}</SubtleBadge>
              : badge
          )}
          <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} className="text-muted font-size-11" />
        </div>
      </div>
      {open && <div>{children}</div>}
    </div>
  );
};

const OrderDetailDrawer = ({
  open,
  onClose,
  orderData,
  conflictMap = {},
  allOrderDayEvents = [],
  onViewAllEmployees,
  t = (k) => k,
  onAssignEmployee,
}) => {
  const [showAllInventory, setShowAllInventory] = useState(false);
  const navigate = useNavigate();

  // ── TRANSLATED MONTH HELPERS (from Absence.js) ──────────────────────────
  const getTranslatedShortMonth = (date) => {
    const monthKey = moment(date).format('MMMM').toLowerCase();
    return t(`shortMonths.${monthKey}`);
  };

  const formatTranslatedDate = (date, includeTime = false) => {
    if (!date) return '--';
    if (includeTime) {
      return moment(date).format('DD-MM-YYYY HH:mm');
    }
    return moment(date).format('DD-MM-YYYY');
  };
  // ────────────────────────────────────────────────────────────────────────

  if (!orderData) return null;

  const raw = orderData?.rawData ?? orderData;

  const templateName = raw?.TemplateName ?? raw?.TaskName ?? '--';
  const clientName = raw?.Relation?.Name ?? orderData?.clientName ?? '--';
  const address = formatAddress(raw?.Relation);

  const startDateStr = orderData?.startDate ?? raw?.WeekStartDate;
  const orderStartDate = orderData?.orderStartDate ?? raw?.StartDate ?? null;
  const orderEndDate = orderData?.orderEndDate ?? raw?.EndDate ?? null;

  const orderStartTime = orderData?.orderStartTime ?? raw?.StartTime ?? null;
  const orderEndTime = orderData?.orderEndTime ?? raw?.EndTime ?? null;
  const fmtTime = (iso) => (iso ? moment(iso).format('HH:mm') : null);
  const startTime = fmtTime(orderStartTime);
  const endTime = orderData?.hasExplicitEndTime ? fmtTime(orderEndTime) : null;
  const timeLabel = startTime && endTime ? `${startTime} – ${endTime}` : startTime ?? '--';

  const eventDate = startDateStr ? moment(startDateStr).format('YYYY-MM-DD') : null;
  const assignedEmployeeIds = [
    ...new Set(
      allOrderDayEvents.map((e) => e?.extendedProps?.rawData?.CleanerId).filter(Boolean)
    ),
  ];
  const totalAssigned = assignedEmployeeIds.length || 1;
  const requiredCount = raw?.RequiredCount ?? totalAssigned;
  const conflictingCount = eventDate
    ? assignedEmployeeIds.filter((id) => conflictMap[id]?.dates?.has(eventDate)).length
    : 0;
  const assignedCount = Math.max(0, totalAssigned - conflictingCount);
  const hasIssue = conflictingCount > 0;
  const isAllClear = !hasIssue;

  const orderStatus = (raw?.OrderStatus ?? 'draft').toString().toLowerCase();

  const getStatusBadge = () => {
    if (hasIssue) {
      return (
        <SubtleBadge pill bg="danger" className="fw-medium font-size-12 d-flex align-items-center gap-1">
          {t('attention')}
        </SubtleBadge>
      );
    }
    const map = { draft: 'primary', '0': 'primary', active: 'success', '1': 'success', completed: 'success', '2': 'success', cancelled: 'danger', '3': 'danger' };
    const bg = map[orderStatus] || 'primary';
    return (
      <SubtleBadge pill bg={bg} className="fw-medium font-size-12 d-flex align-items-center gap-1">
        {t(orderStatus) || 'Ready'}
      </SubtleBadge>
    );
  };

  const handleViewFullDetails = () => {
    if (raw?.OrderId) {
      onClose();
      navigate(`/orders/${raw.OrderId}/details-order`);
    }
  };

  const employeeMap = {};
  allOrderDayEvents.forEach((e) => {
    const cleaner = e?.extendedProps?.rawData?.Cleaner;
    if (cleaner?.UserId) employeeMap[cleaner.UserId] = cleaner;
    else if (e?.extendedProps?.rawData?.CleanerId) {
      const id = e.extendedProps.rawData.CleanerId;
      if (!employeeMap[id]) employeeMap[id] = { UserId: id, Name: id };
    }
  });
  const employeeList = Object.values(employeeMap);
  const visibleEmployees = employeeList.slice(0, 5);
  const extraCount = employeeList.length - 5;

  const rawInventory = raw?.Inventory ?? raw?.InventoryUsed ?? [];
  const DUMMY_INVENTORY = [
    { ItemName: 'Glass Cleaner', Quantity: 2, Unit: 'units', icon: '🧴' },
    { ItemName: 'Mop', Quantity: 1, Unit: 'unit', icon: '🧹' },
    { ItemName: 'Disinfectant', Quantity: 3, Unit: 'units', icon: '🧼' },
    { ItemName: 'Cleaning Bucket', Quantity: 2, Unit: 'units', icon: '🪣' },
    { ItemName: 'Paper Towels', Quantity: 5, Unit: 'units', icon: '🧻' },
  ];
  const inventory = rawInventory.length > 0 ? rawInventory : DUMMY_INVENTORY;
  const visibleInventory = showAllInventory ? inventory : inventory.slice(0, 2);

  const clientNotes = raw?.ClientNotes ?? raw?.Notes ?? '';
  const activityLog = raw?.ActivityLog ?? [];

  return (
    <Offcanvas show={open} onHide={onClose} placement="end" backdrop scroll>
      <div className="d-flex flex-column h-100 bg-white">

        {/* ── HEADER ─────────────────────────────────────────────────────────── */}
        <div className="px-3 pt-3 pb-2 border-bottom">
          <div className="d-flex align-items-center justify-content-between mb-0">
            <div className="d-flex align-items-center gap-2 flex-grow-1 min-width-0">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 fw-semibold font-size-14 bg-success-subtle text-success avatar-35"
              >
                {getInitials(clientName)}
              </div>
              <div className="flex-grow-1 min-width-0">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <span className="fw-semibold font-size-14 text-dark lh-sm">{clientName}</span>
                  {getStatusBadge()}
                </div>
                <div className="font-size-12 text-muted">{templateName}</div>
              </div>
            </div>
            <Button
              variant="link"
              className="p-0 border-0 shadow-none text-secondary flex-shrink-0 ms-2"
              onClick={onClose}
            >
              <FontAwesomeIcon icon={faTimes} />
            </Button>
          </div>
        </div>

        {/* ── SCROLLABLE BODY ─────────────────────────────────────────────────── */}
        <div className="flex-grow-1 overflow-auto scrollbar px-3 pt-3 pb-2">

          {/* ── ORDER SUMMARY ──────────────────────────────────────────────────── */}
          <div className="mb-3">
            <h3 className="fw-medium font-size-15 mb-2">{t('orderSummary')}</h3>
            <div className="row g-3">
              <div className="col-6">
                <p className="mb-0 text-muted fw-medium font-size-13">{t('orderTimeline')}</p>
                <span className="text-muted fw-normal font-size-12">
                  {orderStartDate && orderEndDate
                    ? `${formatTranslatedDate(orderStartDate)} – ${formatTranslatedDate(orderEndDate)}`
                    : '--'}
                </span>
              </div>
              <div className="col-6">
                <p className="mb-0 text-muted fw-medium font-size-13">{t('schedule')}</p>
                <span className="text-muted fw-normal font-size-12">{timeLabel}</span>
              </div>
              <div className="col-6">
                <p className="mb-0 text-muted fw-medium font-size-13">{t('address')}</p>
                <span className="text-muted fw-normal font-size-12 d-block text-break">{address}</span>
              </div>
            </div>
          </div>

          {/* ── ISSUES TO FIX (attention status only) ───────────────── */}
          {hasIssue && (
            <div className="mb-3 rounded-3 p-3 bg-danger-soft border border-danger-subtle">
              {/* Title row */}
              <div className="d-flex align-items-center gap-2 mb-3">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger font-size-13" />
                <span className="fw-bold font-size-14 text-danger">{t('requiredAction')}</span>
              </div>
              <hr className="my-0 mb-3 border-danger-subtle mx-n3 opacity-25" />

              {/* Content row */}
              <div className="d-flex align-items-center justify-content-between gap-3">
                <div>
                  <p className="mb-1 fw-bold font-size-13 text-dark">{t('missingTeamMember')}</p>
                  <p className="mb-0 font-size-11 text-muted">
                    {t('orderRequiresXEmployees', { required: requiredCount, assigned: assignedCount })}
                  </p>
                </div>
                <Button className="fw-semibold flex-shrink-0 text-white bg-danger border-0 rounded-pill font-size-11" disabled>
                  {t('assignedEmployees')}
                </Button>
              </div>
            </div>
          )}

          <hr className="my-0 mb-3 border-300" />

          {/* ── ASSIGNED EMPLOYEES ─────────────────────────────────────────────── */}
          <AccordionSection
            title={t('assignedEmployees')}
            badge={`${assignedCount}/${requiredCount} ${t('assigned')}`}
            badgeBg={isAllClear ? 'success' : 'danger'}
            defaultOpen={true}
          >
            {employeeList.length === 0 ? (
              <p className="text-muted font-size-12 mb-0 py-1">
                {t('noEmployeesAssigned')}
              </p>
            ) : (
              <div>
                <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                  {visibleEmployees.map((emp) => {
                    const isOnLeave = eventDate
                      ? conflictMap[emp.UserId]?.dates?.has(eventDate)
                      : false;

                    const color = isOnLeave
                      ? { bg: '#fecaca', text: '#ef4444' }
                      : getAvatarColor(emp.Name || '');

                    const initials = getInitials(emp.Name || '');

                    const abbreviatedName = (() => {
                      const parts = (emp.Name || '').trim().split(/\s+/).filter(Boolean);
                      if (parts.length === 0) return '';
                      if (parts.length === 1) return parts[0];
                      return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`;
                    })();
                    return (
                      <OverlayTrigger
                        key={emp.UserId}
                        placement="top"
                        overlay={
                          <Tooltip id={`emp-tip-${emp.UserId}`}>
                            {emp.Name || emp.UserId}
                            {isOnLeave ? ` — ${t('onLeave') || 'On leave'}` : ''}
                          </Tooltip>
                        }
                      >
                        <div
                          className={`d-flex align-items-center gap-2 rounded-pill px-2 py-1 fw-medium font-size-12 border cursor-default ${isOnLeave
                              ? 'border-danger-subtle opacity-75'
                              : 'border-300'
                            }`}
                        >
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0 font-size-11 avatar-26"
                            style={{ background: color.bg, color: color.text }}
                          >
                            {initials}
                          </div>
                          <span>
                            {abbreviatedName}
                          </span>
                        </div>
                      </OverlayTrigger>
                    );
                  })}
                  {extraCount > 0 && (
                    <div className="rounded-pill px-2 py-1 fw-medium font-size-12 bg-light text-600 cursor-default">
                      +{extraCount} more
                    </div>
                  )}
                </div>
                <Button
                  variant="link"
                  className="p-0 border-0 shadow-none font-size-12 fw-medium text-decoration-none d-flex align-items-center gap-1 text-700"
                  onClick={() => { onClose(); onViewAllEmployees?.(); }}
                >
                  {t('viewAllEmployees')}
                </Button>
              </div>
            )}
          </AccordionSection>

          <hr className="my-0 mb-3 border-300" />

          {/* ── INVENTORY USED ─────────────────────────────────────────────────── */}
          <AccordionSection
            title={t('inventoryUsed') || 'Inventory Used'}
            badge={
              <SubtleBadge pill bg="primary" className="fw-medium font-size-12 d-flex align-items-center gap-1">
                {t('upcoming')}
              </SubtleBadge>
            }
            defaultOpen={true}
          >
            <div>
              {visibleInventory.map((item, idx) => {
                const isMissing = hasIssue &&
                  (item.ItemName ?? item.name ?? '').toLowerCase().includes('carpet shampoo');
                return (
                  <div
                    key={idx}
                    className={`d-flex align-items-center justify-content-between py-2 px-3 mb-2 rounded-2 border ${isMissing
                        ? 'bg-danger-subtle border-danger-subtle'
                        : 'bg-light border-300'
                      }`}
                  >
                    <div className="d-flex align-items-center gap-2">
                      {item.icon ? (
                        <span className="font-size-18">{item.icon}</span>
                      ) : (
                        <FontAwesomeIcon icon={faBoxOpen} className="text-muted font-size-13" />
                      )}
                      <span
                        className={`font-size-13 fw-medium ${isMissing ? 'text-danger' : ''
                          }`}
                      >
                        {item.ItemName ?? item.name ?? `Item ${idx + 1}`}
                        {isMissing && (
                          <span className="ms-1 fw-normal font-size-12 text-danger">
                            (missing)
                          </span>
                        )}
                      </span>
                    </div>
                    <span
                      className={`font-size-13 fw-semibold ${isMissing ? 'text-danger' : ''
                        }`}
                    >
                      {isMissing
                        ? `0 / ${item.Quantity ?? item.quantity ?? 2}`
                        : `${item.Quantity ?? item.quantity ?? item.units ?? '--'} ${item.Unit ?? item.unit ?? 'units'}`}
                    </span>
                  </div>
                );
              })}

              {inventory.length > 2 && (
                <Button
                  variant="link"
                  className="p-0 border-0 shadow-none font-size-12 fw-medium text-decoration-none text-700"
                  onClick={() => setShowAllInventory((v) => !v)}
                >
                  {showAllInventory
                    ? t('showLess') || 'Show less'
                    : `${t('viewMore') || 'View more'} (${inventory.length - 2})`}
                </Button>
              )}
            </div>
          </AccordionSection>

          <hr className="my-0 mb-3 border-300" />

          {/* ── CLIENT NOTES ──────────────────────────────────────────────────── */}
          <AccordionSection
            title={t('clientNotes') || 'Client Notes'}
            defaultOpen={true}
            badge={
              <SubtleBadge pill bg="primary" className="fw-medium font-size-12 d-flex align-items-center gap-1">
                {t('upcoming')}
              </SubtleBadge>
            }
          >
            <div className="bg-100 border-start border-3 border-success rounded-2 px-3 py-2">
              <p className="text-muted mb-0 fw-normal font-size-12">
                {clientNotes || t('noClientNotes') || 'No note has been added.'}
              </p>
            </div>
          </AccordionSection>

          <hr className="my-0 mb-3 border-300" />

          {/* ── ACTIVITY LOG ──────────────────────────────────────────────────── */}
          <AccordionSection
            title={t('activityLog') || 'Activity Log'}
            defaultOpen={true}
            badge={
              <SubtleBadge pill bg="primary" className="fw-medium font-size-12 d-flex align-items-center gap-1">
                {t('upcoming')}
              </SubtleBadge>
            }
          >
            {activityLog.length === 0 ? (
              <div className="d-flex flex-column gap-1 py-1">
                <div className="d-flex align-items-start gap-2 mb-2">
                  <span className="rounded-circle flex-shrink-0 mt-1 d-inline-block bg-primary dot-8" />
                  <div>
                    <p className="mb-0 fw-semibold font-size-13 text-dark">
                      {t('orderCreated') || 'Order created'}
                    </p>
                    <span className="text-muted font-size-12">
                      {t('admin') || 'Admin'} · {orderStartDate ? formatTranslatedDate(orderStartDate, true) : '--'}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="d-flex flex-column gap-1 py-1">
                {activityLog.map((log, idx) => (
                  <div key={idx} className="d-flex align-items-start gap-2 mb-2">
                    <span
                      className={`rounded-circle flex-shrink-0 mt-1 d-inline-block ${idx === 0 ? 'bg-primary' : 'bg-secondary'
                        } dot-8`}
                    />
                    <div>
                      <p className="mb-0 fw-semibold font-size-13">{log.title}</p>
                      <span className="text-muted font-size-12">
                        {log.by} · {log.time}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </AccordionSection>
        </div>

        {/* ── FOOTER ─────────────────────────────────────────────────────────── */}
        <div className="px-3 pb-3 pt-2 border-top bg-white">
          <Button
            variant="primary"
            className="w-100 fw-semibold rounded-3 font-size-14 py-2 border-0"
            onClick={handleViewFullDetails}
          >
            {t('viewFullDetails')}
          </Button>
        </div>
      </div>
    </Offcanvas>
  );
};

export default OrderDetailDrawer;